import React, { useEffect, useState } from "react";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import useEmailTemplate from "../../data/emailTemplates/useEmailTemplate";

import { useEmailContent } from "../../data/emailContent/useEmailContent";
import { useForm } from "react-hook-form";
import { StyledTextInput } from "../../components/Shared/StyledInputs/StyledTextInput";
import TinyMCE from "../../components/Shared/TinyMCE";
import Flyout, { FlyoutControlProps } from "../../containers/Flyout";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { useEmailContentMutations } from "../../data/emailContent/useEmailContentMutations";
import ConfirmModal from "../Shared/ConfirmModal";
import { SendTestModal } from "./SendTestModal";
import { convertSubsToNested } from "../../utils/convertSubsToMergeTags";
import useUserContext from "../../contexts/UserContext";

interface EditEmailTemplateFlyoutProps extends FlyoutControlProps {
  agencyId: string;
  emailTemplateId: string;
}

interface EmailTemplateFormFields {
  subject: string;
  body: string;
}

export function EditEmailTemplateFlyout({ open, setOpen, agencyId, emailTemplateId }: EditEmailTemplateFlyoutProps) {
  const { user: { agency } } = useUserContext();
  const { emailTemplate } = useEmailTemplate(emailTemplateId);
  const { emailContent } = useEmailContent(agencyId, emailTemplateId);
  const { update, remove } = useEmailContentMutations(agencyId, emailTemplateId);
  const { register, getValues, setValue, handleSubmit, reset } = useForm<EmailTemplateFormFields>();
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [sendTestOpen, setSendTestOpen] = useState(false);


  const baseMergeTags = [
    { label: "First Name", value: "person.firstName" },
    { label: "Last Name", value: "person.lastName" },
    { label: "Email Address", value: "person.Email" },
    { label: "Name", value: "agency.Name" },
    { label: "Domain", value: "agency.Domain" },
    { label: "Telephone Number", value: "agency.TelephoneNumber" }
  ]

  const valuationMergeTags = [
    { label: "House Number", value: "valuation.HouseNumber" },
    { label: "Street", value: "valuation.Street" },
    { label: "Town", value: "valuation.Town" },
    { label: "Postcode", value: "valuation.Postcode" },
    { label: "Formatted Address", value: "valuation.AddressFormatted" },
    { label: "Bedrooms", value: "valuation.NoBedrooms" },
    { label: "Bathrooms", value: "valuation.NoBathrooms" },
    { label: "Looking To Move", value: "valuation.LookingToMove" },
    { label: "Sale Value", value: "valuation.SaleValue" },
    { label: "Sale Value (Min)", value: "valuation.SaleValueMin" },
    { label: "Sale Value (Max)", value: "valuation.SaleValueMax" },
    { label: "Rental Value", value: "valuation.RentalValue" },
    { label: "Rental Value (Min)", value: "valuation.RentalValueMin" },
    { label: "Rental Value (Max)", value: "valuation.RentalValueMax" },
    { label: "Appointment Time and Date", value: "valuation.AppointmentDateTime" },
    { label: "Appointment Date Only", value: "valuation.AppointmentDate" },
    { label: "Appointment Time Only", value: "valuation.AppointmentTime" },
  ]

  const getAdditionalMergeTags = (emailTemplateId: string) => {
    switch (emailTemplateId) {
      case "797e94c6-fc51-4601-9483-83237bacce97": // Customer Appraisal
      case "4f0a6c7c-0bf5-4750-8362-af4a06a32760": // Updated Instant Valutation
      case "9890c018-b4fd-481d-9f3c-22121b6287e5": // Valutation Reminder
      case "a5117b70-fe0c-497c-a62c-ed97538dfdca": // Time to Move
      case "275977c8-50a8-42f2-afd1-812e49608f2b": // Valuation Time Updated
        return valuationMergeTags;

      case "11fcf175-ea19-4231-80e9-fc95b4dd2ce3": // New Instant Valutation
        return [
          ...valuationMergeTags,
          { label: "Widget", value: "valuation.Widget" }
        ];

      case "a15be394-b509-436b-a072-50c1c800e57b": // Lettings Qualifier
      case "e14795c2-89ed-4dc8-a490-465dfea5bd4b": // Sales Qualifier
      case "ed01d1d9-a7c0-40f8-aa38-a9a28f7327d5": // Viewing Time Updated
        return [
          { label: "Title", value: "property.Title" },
          { label: "Name/Number", value: "property.AddressNameNumber" },
          { label: "Street", value: "property.AddressStreet" },
          { label: "Line 2", value: "property.AddressTwo" },
          { label: "Line 3", value: "property.AddressThree" },
          { label: "Line 4", value: "property.AddressFour" },
          { label: "Postcode", value: "property.AddressPostcode" },
          { label: "Formatted Address", value: "property.AddressFormatted" },
          { label: "Bedrooms", value: "property.Bedrooms" },
          { label: "Bathrooms", value: "property.Bathrooms" },
          { label: "Price", value: "property.Price" },
          { label: "Type", value: "property.Type" },
          { label: "Link", value: "property.Link" },
          { label: "Source", value: "viewing.Source" },
          { label: "Appointment Date", value: "viewing.Date" },
          { label: "Appointment Time", value: "viewing.Time" }
        ];
      case "":
        return [
          { label: "Review Site", value: "review.SiteName" },
          { label: "Review Type", value: "review.ReviewType" },
          { label: "Completion Date", value: "review.CompletionDate" }
        ]
      default:
        return [];
    }
  };

  const updateContent = (data: EmailTemplateFormFields) => {
    update.mutate(data);
  };

  useEffect(() => {
    if (
      !emailTemplate.isLoading &&
      !emailContent.isLoading &&
      emailTemplate.data?.data &&
      emailContent.data?.data
    ) {
      reset({
        subject: emailContent.data.data.subject,
        body: emailContent.data.data.body,
      })
    }
  }, [
    emailTemplate.isLoading,
    emailTemplate.data,
    emailContent.isLoading,
    emailContent.data,
    reset
  ])

  return (
    <>
      <SendTestModal
        open={sendTestOpen}
        setOpen={setSendTestOpen}
        templateId={emailTemplateId}
      />
      <ConfirmModal
        open={confirmResetOpen}
        setOpen={setConfirmResetOpen}
        title="Reset Email Template"
        message="Are you sure you want to reset this email template to the default content? This action cannot be undone."
        confirmButtonText="Reset"
        onConfirm={() => {
          remove.mutate();
          setConfirmResetOpen(false);
        }}
      />
      <Flyout
        disableClickAway
        open={open}
        setOpen={setOpen}
        title="Edit Email Template"
      >
        {emailTemplate.isLoading || emailContent.isLoading ?
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="h-80 max-w-7xl flex items-center justify-center">
              <LoadingWheel width="w-12" height="h-12" />
            </div>
          </div>
          :
          <>
            <div className="mb-6 ">
              <p>You can use this to edit and customise the emails sent from the platform to clients. Call-To-Action buttons where relevant are added automatically. For any changes please save the template first and then send a test to view the email in full.</p>
            </div>
            <form onSubmit={handleSubmit(updateContent)} className="space-y-6">
              <StyledTextInput
                label="Subject"
                register={register}
                registerName="subject"
              />
              <div>
                <label className="mb-1 text-sm font-medium text-gray-700">
                  Content
                </label>
                <TinyMCE
                  // @ts-ignore
                  statusBar={false}
                  // @ts-ignore
                  height={400}
                  // @ts-ignore
                  value={getValues(`body`)}
                  // @ts-ignore
                  onChange={(content: any) => setValue(`body`, content)}
                  // @ts-ignore
                  mergeTags={convertSubsToNested([...baseMergeTags, ...getAdditionalMergeTags(emailTemplateId)])}
                />
                <div className="rounded border-dashed border-2 mt-1 text-gray-600 border-gray-300 -pl-4 p-4">
                  <div className="bg-gray-200 -m-4 mb-4 p-1.5 px-3 text-sm font-medium ">
                    Email Header & Footer is fixed and cannot be edited.
                    <br />
                    <br />
                    <span className="italic">Footer Content</span>
                  </div>
                  <>
                    <tr>
                      <td
                        align="left"
                        style={{ padding: "0px 0px 20px 0px" }}
                      >
                        <div
                          style={{
                            textAlign: "left",
                            paddingLeft: "12px"
                          }}
                        >
                          Thank you
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td
                        align="left"
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            lineHeight: "22px",
                            paddingBottom: "40px",
                            paddingLeft: "12px"
                          }}
                        >
                          {agency.name}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td
                      // align="center"
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            lineHeight: "16px",
                            textAlign: "center",
                          }}
                        >
                          This email was sent to{" "}
                          <a href="mailto:test@email.com">test@email.com</a> from {agency.name}. You may unsubscribe at any time using the{" "}
                          <a href="#" className="text-blue-600 underline">
                            unsubscribe link
                          </a>
                          .
                        </div>
                      </td>
                    </tr>
                  </>
                </div>
                <div className="mt-4 flex space-x-4">
                  {/* @ts-ignore */}
                  <ButtonInput
                    classes="bg-gray-200 hover:bg-gray-300 text-gray-700"
                    isSubmit={false}
                    onClick={() => setConfirmResetOpen(true)}
                    label="Reset to Default"
                  />
                  {/* @ts-ignore */}
                  <ButtonInput
                    isSubmit
                    label="Save Template"
                  />
                  {/* @ts-ignore */}
                  <ButtonInput
                    isSubmit={false}
                    label="Send Test"
                    onClick={() => setSendTestOpen(true)}
                  />
                </div>
              </div>
            </form>
          </>
        }
      </Flyout>
    </>
  );
}

